import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FiExternalLink, FiPause, FiPlay } from 'react-icons/fi';
import TopBar from '../components/TopBar';
import ResolvedStorageImage from '../components/ResolvedStorageImage';
import { UserContext } from '../App';
import { db, functions } from '../firebase';
import layoutStyles from '../styles/layout.module.css';
import styles from './Artists.module.css';
import { formatCountdown, formatPrice, formatProductSaleStatus, getProductCountdown, getProductInventory, getProductSaleStatus } from '../utils/marketplace';
import { getArtistPhotoPath, useStorageUrl, useStorageUrls } from '../utils/storageMedia';
import { getCompactCollectibleImagePath } from '../utils/collectibleMedia';
import { applyMetadata, buildArtistMetadata, getDefaultRouteMetadata } from '../seo/metadata';
import { buildAuthNavigationTarget, buildReturnToPath } from '../auth/returnTo';

function normalizeUrl(value) {
  if (!value || typeof value !== 'string') return '';
  if (value.startsWith('http://') || value.startsWith('https://')) return value;
  return `https://${value}`;
}

const SOCIAL_KEYS = ['instagram', 'twitter', 'tiktok', 'youtube', 'website'];

function truncateText(value, limit) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= limit) return text;
  return `${text.slice(0, limit).trimEnd()}…`;
}

export default function Artists() {
  const appUser = useContext(UserContext);
  const { artistId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const audioRef = useRef(null);
  const [artist, setArtist] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);
  const [isFan, setIsFan] = useState(false);
  const [fanBusy, setFanBusy] = useState(false);
  const [fanError, setFanError] = useState('');
  const [nowMs, setNowMs] = useState(Date.now());
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [artistDescriptionExpanded, setArtistDescriptionExpanded] = useState(false);
  const returnTo = buildReturnToPath(location);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    setExpandedDescriptions({});
    setArtistDescriptionExpanded(false);
  }, [artistId]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const [artistSnap, productsSnap] = await Promise.all([
          getDoc(doc(db, 'artists', String(artistId))),
          getDocs(query(collection(db, 'products'), where('artistId', '==', String(artistId)), where('status', '==', 'active'))),
        ]);
        if (!active) return;
        if (!artistSnap.exists()) {
          setArtist(null);
          setProducts([]);
          setError('Artist not found.');
          return;
        }
        const artistData = artistSnap.data() || {};
        setArtist({ id: artistSnap.id, ...artistData });
        setProducts(productsSnap.docs
          .map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }))
          .sort((left, right) => Number(right.createdAt?.seconds || 0) - Number(left.createdAt?.seconds || 0)));
        setError('');
      } catch (err) {
        if (!active) return;
        setArtist(null);
        setProducts([]);
        setError(err?.message || 'Unable to load artist.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [artistId]);

  useEffect(() => {
    if (!artistId) return;
    if (artist) {
      applyMetadata(buildArtistMetadata({ artist, artistId }));
      return;
    }
    applyMetadata({
      ...getDefaultRouteMetadata(`/artists/${artistId}`),
      pathname: `/artists/${artistId}`,
    });
  }, [artist, artistId]);

  useEffect(() => {
    if (!appUser?.id || !artistId) {
      setIsFan(false);
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'artists', String(artistId), 'fans', appUser.id));
        if (active) setIsFan(snap.exists());
      } catch {
        if (active) setIsFan(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [appUser?.id, artistId]);

  const handleBack = useCallback(() => {
    if (location.state?.fromCreator) {
      navigate('/creator');
      return;
    }
    if (window.history.length > 2) {
      navigate(-1);
      return;
    }
    navigate(appUser ? '/home' : '/');
  }, [appUser, location.state, navigate]);

  const toggleAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  }, []);

  const toggleFan = async () => {
    if (!appUser?.id) {
      setFanError('');
      navigate(buildAuthNavigationTarget('/signin', returnTo), { state: { returnTo } });
      return;
    }
    setFanBusy(true);
    setFanError('');
    try {
      const callable = httpsCallable(functions, 'toggleArtistFan');
      const result = await callable({ artistId });
      const followed = Boolean(result?.data?.followed);
      setIsFan(followed);
      setArtist((prev) => prev ? { ...prev, fanCount: Math.max(0, Number(prev.fanCount || 0) + (followed ? 1 : -1)) } : prev);
    } catch (err) {
      setFanError(err?.message || 'Unable to update fan status.');
    } finally {
      setFanBusy(false);
    }
  };

  const socialLinks = useMemo(() => {
    if (!artist) return [];
    return SOCIAL_KEYS.map((key) => {
      const value = normalizeUrl(artist[key]);
      if (!value) return null;
      return { key, value };
    }).filter(Boolean);
  }, [artist]);
  const artistAudioStorageUrl = useStorageUrl(artist?.artistAudioPath || '');
  const artistAudioSrc = artist?.artistAudioUrl || artistAudioStorageUrl || '';

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnd = () => setPlaying(false);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('ended', onEnd);
    };
  }, [artistAudioSrc]);

  const artistDescription = String(artist?.artistDescription || 'This artist has not added a public description yet.').trim();
  const artistDescriptionNeedsToggle = artistDescription.length > 200;
  const artistDescriptionVisible = artistDescriptionExpanded || !artistDescriptionNeedsToggle
    ? artistDescription
    : truncateText(artistDescription, 200);
  const visibleProducts = useMemo(
    () => products.filter((product) => getProductSaleStatus(product, nowMs) !== 'ended'),
    [nowMs, products]
  );
  const visibleProductPaths = useMemo(
    () => visibleProducts.map((product) => getCompactCollectibleImagePath(product)).filter(Boolean),
    [visibleProducts]
  );
  const visibleProductUrls = useStorageUrls(visibleProductPaths);

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar variant="back" backLabel="Back" onBack={handleBack} rightActionLabel="Home" onRightAction={() => navigate('/home')} />
      <div className={layoutStyles.detailMain}>
        <div className={styles.page}>
          {loading ? <p className={styles.state}>Loading artist…</p> : null}
          {!loading && error ? <p className={styles.error}>{error}</p> : null}
          {!loading && !error && artist ? (
            <>
              <section className={styles.hero}>
                <div className={styles.heroVisual}>
                  <div className={styles.portraitWrap}>
                    <ResolvedStorageImage path={getArtistPhotoPath(artist, 'full')} alt={artist.artistFullName || 'Artist'} fallback={<div className={styles.placeholder}>Artist</div>} />
                  </div>
                  {artistAudioSrc ? (
                    <>
                      <button type="button" className={styles.audioButton} onClick={toggleAudio}>
                        {playing ? <FiPause /> : <FiPlay />}
                        <span>{playing ? 'Pause audio' : 'Play audio'}</span>
                      </button>
                      <audio ref={audioRef} src={artistAudioSrc} preload="none" />
                    </>
                  ) : null}
                </div>

                <div className={styles.heroCard}>
                  <div className={styles.heroTopline}>
                    <span className={styles.eyebrow}>Public Artist Page</span>
                  </div>
                  <h1>{artist.artistFullName || 'Untitled Artist'}</h1>
                  <div className={styles.heroDescriptionBlock}>
                    <p className={styles.heroCopy}>{artistDescriptionVisible}</p>
                    {artistDescriptionNeedsToggle ? (
                      <button
                        type="button"
                        className={styles.heroReadMoreButton}
                        onClick={() => setArtistDescriptionExpanded((prev) => !prev)}
                      >
                        {artistDescriptionExpanded ? 'Read less' : 'Read more'}
                      </button>
                    ) : null}
                  </div>

                  <div className={styles.actionRow}>
                    <button type="button" className={`${styles.primaryButton} ${isFan ? styles.activeButton : ''}`.trim()} onClick={toggleFan} disabled={fanBusy}>
                      {fanBusy ? 'Updating…' : isFan ? 'Following' : 'Become a Fan'}
                    </button>
                  </div>

                  {fanError ? <p className={styles.error}>{fanError}</p> : null}

                  {socialLinks.length ? (
                    <div className={styles.socials}>
                      {socialLinks.map((link) => (
                        <a key={link.key} href={link.value} target="_blank" rel="noreferrer">
                          <span>{link.key}</span>
                          <FiExternalLink />
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              </section>

              <section className={styles.catalogSection}>
                <div className={styles.sectionHeader}>
                  <div>
                    <span className={styles.sectionEyebrow}>Live Collectibles</span>
                    <h2>Current collectible lineup</h2>
                  </div>
                </div>

                {visibleProducts.length ? (
                  <div className={styles.grid}>
                    {visibleProducts.map((product) => {
                      const inventory = getProductInventory(product);
                      const countdown = getProductCountdown(product, nowMs);
                      const saleStatus = getProductSaleStatus(product, nowMs);
                      const saleLabel = formatProductSaleStatus(product, nowMs);
                      const fullDescription = String(product.description || 'No description added yet.').trim();
                      const isExpanded = Boolean(expandedDescriptions[product.id]);
                      const needsReadMore = fullDescription.length > 40;
                      const visibleDescription = isExpanded || !needsReadMore
                        ? fullDescription
                        : truncateText(fullDescription, 40);
                      return (
                        <button key={product.id} type="button" className={styles.card} onClick={() => navigate(`/products/${product.id}`)}>
                          <div className={styles.cardMedia}>
                            {visibleProductUrls[getCompactCollectibleImagePath(product)] ? <img src={visibleProductUrls[getCompactCollectibleImagePath(product)]} alt={product.title || 'Collectible'} /> : <div className={styles.cardPlaceholder}>Collectible</div>}
                          </div>
                          <div className={styles.cardBody}>
                            <div className={styles.cardTopline}>
                              <span className={`${styles.statusBadge} ${saleStatus === 'active' ? styles.statusBadgeLive : ''}`.trim()}>{saleLabel}</span>
                              {saleStatus !== 'ended' ? <span className={styles.availability}>{inventory.availableCount} available</span> : null}
                            </div>
                            <h3>{product.title || product.id}</h3>
                            <div className={styles.descriptionBlock}>
                              <p>{visibleDescription}</p>
                              {needsReadMore ? (
                                <span
                                  role="button"
                                  tabIndex={0}
                                  className={styles.readMoreButton}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setExpandedDescriptions((prev) => ({ ...prev, [product.id]: !prev[product.id] }));
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      setExpandedDescriptions((prev) => ({ ...prev, [product.id]: !prev[product.id] }));
                                    }
                                  }}
                                >
                                  {isExpanded ? 'Read less' : 'Read more'}
                                </span>
                              ) : null}
                            </div>
                            {countdown ? (
                              <div className={`${styles.countdownRow} ${countdown.status === 'active' ? styles.countdownRowLive : ''}`.trim()}>
                                <span>{countdown.label}</span>
                                <strong>{formatCountdown(countdown.targetMs, nowMs)}</strong>
                              </div>
                            ) : null}
                            <div className={styles.priceRow}>
                              <span className={styles.priceTag}>{formatPrice(product)}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className={styles.emptyState}>
                    <span className={styles.emptyBadge}>No live collectibles</span>
                    <h3>This artist has no live collectibles at the moment.</h3>
                    <p>Check back later for future releases, scheduled drops, or collectible launches from this artist.</p>
                  </div>
                )}
              </section>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
